import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCw, Download, ShieldAlert } from "lucide-react";
import api, { detailText } from "@/lib/api";
import { toast } from "sonner";

/**
 * SHADOW LIVE — the broker validation comparison table.
 *
 * One row per intended broker action: what GroundZero meant to send, beside what Zerodha
 * actually answered. The account has its funds withdrawn, so every order is expected to come
 * back REJECTED; the strategy then advances on a SIMULATED fill at the intended LIMIT price so
 * the ladder can walk through every pair and level.
 *
 * NOTHING ON THIS PAGE IS A REAL POSITION, A REAL FILL OR REAL P&L. The page says so, everywhere
 * and permanently, because the one way this screen could do harm is by being mistaken for the
 * LIVE one.
 */
const LABEL = "SHADOW LIVE / SIMULATED STRATEGY FILL / REAL BROKER REJECTION";

const OUTCOME_STYLE = {
  REJECTED_AS_EXPECTED: "bg-success-bg text-success-fg border-success-fg/30",
  UNEXPECTED_BROKER_STATE: "bg-danger-bg text-danger-fg border-danger-fg/50",
  AWAITING_BROKER: "bg-warning-bg text-warning-fg border-warning-fg/40",
  NOT_SENT: "bg-neutral-bg text-fg-secondary border-line",
};

const OUTCOME_LABEL = {
  REJECTED_AS_EXPECTED: "Rejected (expected)",
  UNEXPECTED_BROKER_STATE: "NOT REJECTED",
  AWAITING_BROKER: "Awaiting broker",
  NOT_SENT: "Not sent",
};

function num(v, dp = 2) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-IN", { minimumFractionDigits: dp, maximumFractionDigits: dp }) : String(v);
}

function time(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleTimeString("en-IN", { hour12: false });
}

function Chip({ children, cls }) {
  return (
    <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {children}
    </span>
  );
}

/** What this shadow account is allowed to trade, and how that is decided. */
function InstrumentScope({ allowlist }) {
  const [open, setOpen] = useState(false);
  if (!allowlist) return <Field label="Instruments" value="—" />;
  const master = allowlist.mode === "ALL_MCX_NFO";
  const dynamic = master || allowlist.mode === "ALL_VERIFIED_MCX";
  const names = master
    ? Object.entries(allowlist.master_counts || {}).map(([ex, n]) => `${ex}: ${n} contracts`)
    : (allowlist.verified_instruments || []).map((i) => i.underlying);
  const shown = dynamic ? names : allowlist.explicit || [];
  return (
    <div className="relative flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-wide text-fg-secondary">Instruments</span>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`mono text-left text-[12px] font-semibold ${dynamic ? "text-warning-fg" : "text-fg"} hover:underline`}
        title={allowlist.note}
        data-testid="shadow-allowlist-mode"
      >
        {master ? `ALL MCX + NFO (${allowlist.count})`
          : dynamic ? `ALL VERIFIED MCX (${allowlist.count})`
          : `EXPLICIT (${(allowlist.explicit || []).length})`}
      </button>
      {open && (
        <div className="absolute top-full left-0 z-20 mt-1 max-h-72 w-80 overflow-auto rounded border border-line bg-surface p-3 shadow-xl">
          <div className="mb-2 text-[11px] leading-relaxed text-fg-secondary">{allowlist.note}</div>
          <div className="flex flex-wrap gap-1">
            {shown.map((n) => (
              <span key={n} className="rounded border border-line bg-surface-muted px-1.5 py-0.5 mono text-[10px] text-fg">{n}</span>
            ))}
            {!shown.length && <span className="text-[11px] text-fg-secondary">Nothing is allowed to trade.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, cls }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-wide text-fg-secondary">{label}</span>
      <span className={`mono text-[12px] font-semibold ${cls || "text-fg"}`}>{value}</span>
    </div>
  );
}

/** The banner that must never be missed: a shadow order was NOT refused. */
function HaltBanner({ halt, onCleared }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!halt?.halted) return null;

  const clear = async () => {
    if (note.trim().length < 5) {
      toast.error("Say what you checked at Zerodha before clearing the halt.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/shadow-live/halt/clear", { note: note.trim() });
      toast.success("Shadow halt cleared.");
      setNote("");
      onCleared?.();
    } catch (e) {
      toast.error(detailText(e, "Could not clear the halt."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border-2 border-danger-fg/50 bg-danger-bg p-4" data-testid="shadow-halt">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger-fg" />
        <div className="flex-1 space-y-2">
          <div className="text-sm font-bold uppercase tracking-wide text-danger-fg">
            Shadow submission stopped — an order was not rejected
          </div>
          <p className="text-[12px] leading-relaxed text-danger-fg">
            {halt.reason}. It may be live at Zerodha right now. Nothing further is sent — not an
            entry, not a hedge, not an exit — until someone checks the Zerodha order book and
            clears this. Check Kite first; clearing it here changes nothing at the broker.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What did you find in Kite?"
              className="min-w-[280px] flex-1 rounded border border-danger-fg/50 bg-surface px-2 py-1 text-[12px] text-fg outline-none focus:border-[var(--focus)]"
              data-testid="shadow-halt-note"
            />
            <button
              onClick={clear}
              disabled={busy}
              className="rounded border border-danger-fg/50 bg-danger-bg px-3 py-1 text-[12px] font-semibold text-danger-fg hover:brightness-95 disabled:opacity-50"
              data-testid="shadow-halt-clear"
            >
              {busy ? "Clearing…" : "I checked Kite — clear the halt"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_FILTERS = {
  strategy_id: "", underlying: "", level_no: "", pair_no: "", leg_role: "", action: "", outcome: "",
};

export default function ShadowLive() {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null)
      );
      const [s, l] = await Promise.all([
        api.get("/shadow-live/status"),
        api.get("/shadow-live/ledger", { params }),
      ]);
      setStatus(s.data);
      setRows(l.data.rows || []);
      setSummary(l.data.summary || null);
    } catch (e) {
      toast.error(detailText(e, "Could not load the shadow ledger."));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // The team compares at the desk, so give them the table as a file too.
  const exportCsv = () => {
    if (!rows.length) return;
    const cols = [
      "created_at", "strategy_id", "trade_name", "level_no", "pair_no", "leg_role", "action",
      "shadow_trigger_at", "shadow_trigger_spread", "exchange", "tradingsymbol", "shadow_expiry",
      "transaction_type", "lots", "broker_quantity", "requested_price", "limit_price",
      "product", "order_type", "validity", "broker_tag", "submitted_at", "broker_order_id",
      "broker_status", "status_message", "reject_reason", "broker_order_timestamp",
      "broker_exchange_timestamp", "filled_quantity", "shadow_fill_price",
      "shadow_filled_quantity", "shadow_filled_at", "outcome",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `shadow-live-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const unexpected = summary?.unexpected_broker_state || 0;
  const filterFields = useMemo(() => ([
    { key: "underlying", placeholder: "Instrument" },
    { key: "strategy_id", placeholder: "Trade id" },
    { key: "level_no", placeholder: "Level", width: "w-20" },
    { key: "pair_no", placeholder: "Pair", width: "w-20" },
  ]), []);

  return (
    <div className="space-y-4 p-4 text-fg" data-testid="shadow-live-page">
      {/* The label, permanently, at the top of everything. */}
      <div className="rounded-lg border border-warning-fg/40 bg-warning-bg p-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning-fg" />
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-warning-fg">{LABEL}</div>
            <p className="mt-1 text-[12px] leading-relaxed text-warning-fg">
              These are <strong>real Zerodha orders</strong> sent from the live strategy on an
              account with its funds withdrawn, so that they are <strong>refused</strong> and can
              be compared against the Zerodha Order Book. The strategy then continues on a{" "}
              <strong>simulated fill</strong> at the intended limit price. Nothing here is a real
              position, a real fill or real P&amp;L, and none of it reaches normal LIVE
              reconciliation.
            </p>
          </div>
        </div>
      </div>

      <HaltBanner halt={status?.halt} onCleared={load} />

      {/* status + counts */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-line bg-surface-muted p-3">
        <Field
          label="Shadow mode"
          value={status?.shadow_trading_enabled ? "ARMED" : "OFF"}
          cls={status?.shadow_trading_enabled ? "text-warning-fg" : "text-fg-secondary"}
        />
        <Field
          label="Normal live"
          value={status?.live_trading_enabled ? "ON" : "OFF"}
          cls={status?.live_trading_enabled ? "text-danger-fg" : "text-success-fg"}
        />
        <Field label="Account" value={status?.broker_user_id || "—"} />
        {status?.trading_products !== "OPTIONS_ONLY" && <>
          <Field
            label="Futures shadow"
            value={status?.account_shadow_enabled ? "ENABLED" : "OFF"}
            cls={status?.account_shadow_enabled ? "text-warning-fg" : "text-fg-secondary"}
          />
          <InstrumentScope allowlist={status?.allowlist} />
          <Field
            label="Futures max per order"
            value={status?.quantity_source === "BROKER_MASTER"
              ? `${status?.max_shadow_lots_per_order ?? 1} broker lot`
              : status ? `${status.max_broker_quantity_per_order ?? "—"} qty` : "—"}
          />
        </>}
        {status?.trading_products && status.trading_products !== "FUTURES_ONLY" && <>
          <Field
            label="Options shadow"
            value={status?.account_options_shadow_enabled ? "ENABLED" : "OFF"}
            cls={status?.account_options_shadow_enabled ? "text-warning-fg" : "text-fg-secondary"}
          />
          <Field
            label="Options instruments"
            value={status?.options_instrument_access === "SELECTED"
              ? ((status?.options_allowlist || []).join(", ") || "none selected") : "All NFO stock options"}
          />
          <Field label="Options max per order" value={`${status?.options_max_lots_per_order ?? 1} lot`} />
        </>}
        <div className="h-8 w-px bg-line" />
        <Field label="Orders" value={summary?.orders ?? "—"} />
        <Field label="Reached broker" value={summary?.reached_broker ?? "—"} />
        <Field
          label="Rejected as expected"
          value={summary?.rejected_as_expected ?? "—"}
          cls="text-success-fg"
        />
        <Field label="Simulated fills" value={summary?.simulated_fills ?? "—"} />
        <Field
          label="NOT rejected"
          value={unexpected}
          cls={unexpected ? "text-danger-fg" : "text-fg-secondary"}
        />
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 rounded border border-line px-2.5 py-1 text-[12px] text-fg hover:bg-line"
            data-testid="shadow-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={exportCsv}
            disabled={!rows.length}
            className="flex items-center gap-1.5 rounded border border-line px-2.5 py-1 text-[12px] text-fg hover:bg-line disabled:opacity-40"
            data-testid="shadow-export"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        </div>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2">
        {filterFields.map((f) => (
          <input
            key={f.key}
            value={filters[f.key]}
            onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
            placeholder={f.placeholder}
            className={`${f.width || "w-40"} rounded border border-line bg-surface px-2 py-1 text-[12px] text-fg outline-none focus:border-[var(--focus)]`}
            data-testid={`shadow-filter-${f.key}`}
          />
        ))}
        {[
          { key: "leg_role", options: ["", "FAR", "NEAR"], label: "Leg" },
          { key: "action", options: ["", "ENTRY", "EXIT"], label: "Action" },
          { key: "outcome", options: ["", ...Object.keys(OUTCOME_LABEL)], label: "Outcome" },
        ].map((s) => (
          <select
            key={s.key}
            value={filters[s.key]}
            onChange={(e) => setFilters({ ...filters, [s.key]: e.target.value })}
            className="rounded border border-line bg-surface px-2 py-1 text-[12px] text-fg outline-none focus:border-[var(--focus)]"
            data-testid={`shadow-filter-${s.key}`}
          >
            {s.options.map((o) => (
              <option key={o} value={o}>{o ? (OUTCOME_LABEL[o] || o) : `All ${s.label.toLowerCase()}s`}</option>
            ))}
          </select>
        ))}
        <button
          onClick={() => setFilters(EMPTY_FILTERS)}
          className="rounded border border-line px-2.5 py-1 text-[12px] text-fg-secondary hover:bg-line"
        >
          Clear
        </button>
      </div>

      {/* the comparison */}
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[1400px] text-[12px]">
          <thead>
            <tr className="bg-surface-muted text-[10px] uppercase tracking-wide text-fg-secondary">
              <th colSpan={9} className="border-r border-line px-3 py-2 text-left">
                GroundZero intended
              </th>
              <th colSpan={6} className="px-3 py-2 text-left text-warning-fg">
                Zerodha actual
              </th>
              <th className="px-3 py-2 text-left">Shadow fill</th>
            </tr>
            <tr className="bg-surface-muted text-[10px] uppercase tracking-wide text-fg-secondary">
              {["Time", "Level / Pair", "Leg", "Action", "Contract", "Side", "Qty", "LTP", "Limit"].map((h) => (
                <th key={h} className="px-3 py-1.5 text-left font-medium">{h}</th>
              ))}
              <th className="border-l border-line px-3 py-1.5 text-left font-medium">Order id</th>
              {["Status", "Message", "Filled", "Broker time", "Outcome"].map((h) => (
                <th key={h} className="px-3 py-1.5 text-left font-medium">{h}</th>
              ))}
              <th className="border-l border-line px-3 py-1.5 text-left font-medium">Simulated @</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((r) => (
              <tr
                key={r.id}
                className="gz-row"
                data-tone={r.outcome === "UNEXPECTED_BROKER_STATE" ? "danger" : undefined}
                data-testid="shadow-row"
              >
                <td className="mono px-3 py-1.5 text-fg-secondary">{time(r.created_at)}</td>
                <td className="mono px-3 py-1.5 text-fg">L{r.level_no ?? "—"} / P{r.pair_no ?? "—"}</td>
                <td className="px-3 py-1.5">
                  <Chip cls={r.leg_role === "FAR" ? "bg-info-bg text-info-fg border-info-fg/30" : "bg-neutral-bg text-neutral-fg border-line"}>
                    {r.leg_role || "—"}
                  </Chip>
                </td>
                <td className="px-3 py-1.5 text-fg-secondary">{r.action}</td>
                <td className="mono px-3 py-1.5 text-fg">
                  {r.tradingsymbol || "—"}
                  {r.shadow_expiry ? <span className="ml-1 text-[10px] text-fg-secondary">{r.shadow_expiry}</span> : null}
                </td>
                <td className={`px-3 py-1.5 font-semibold ${r.transaction_type === "SELL" ? "text-danger-fg" : "text-success-fg"}`}>
                  {r.transaction_type}
                </td>
                <td className="mono px-3 py-1.5 text-fg">{r.broker_quantity ?? "—"}</td>
                <td className="mono px-3 py-1.5 text-fg-secondary">{num(r.requested_price)}</td>
                <td className="mono px-3 py-1.5 font-semibold text-fg">{num(r.limit_price)}</td>

                <td className="mono border-l border-line px-3 py-1.5 text-warning-fg">
                  {r.broker_order_id || "—"}
                </td>
                <td className="mono px-3 py-1.5 text-fg">{r.broker_status || "—"}</td>
                <td className="max-w-[280px] truncate px-3 py-1.5 text-fg-secondary" title={r.status_message || r.reject_reason || ""}>
                  {r.status_message || r.reject_reason || "—"}
                </td>
                <td className="mono px-3 py-1.5 text-fg-secondary">{r.filled_quantity ?? 0}</td>
                <td className="mono px-3 py-1.5 text-fg-secondary">{r.broker_order_timestamp || "—"}</td>
                <td className="px-3 py-1.5">
                  <Chip cls={OUTCOME_STYLE[r.outcome] || OUTCOME_STYLE.NOT_SENT}>
                    {OUTCOME_LABEL[r.outcome] || r.outcome}
                  </Chip>
                </td>

                <td className="mono border-l border-line px-3 py-1.5 text-fg-secondary">
                  {r.shadow_simulated_fill ? `${num(r.shadow_fill_price)} x${r.shadow_filled_quantity}` : "—"}
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td colSpan={16} className="px-3 py-10 text-center text-fg-secondary">
                  No shadow orders yet. Create a trade with environment SHADOW on the validation
                  account and start it.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
